/**
 * What the findings queue counts as a finding, and how each row reads.
 *
 * The queue lists every compliance gap: regulatory findings and the two
 * suggestion kinds that are gaps (a control amendment, a missing document).
 * These rules were the web's own (`components/regulation/finding/findingItem`);
 * they live here so the queue, Home, and anything else that reports findings
 * — eve's summary email among them — count and label the same gaps.
 *
 * Status speaks the redesign's language: open, resolved, dismissed. A finding
 * "closed" with a reason and a suggestion "dismissed" are both dismissed; a
 * suggestion "accepted" (the retired in-progress stage) is still open.
 *
 * Pure, structural inputs: the web's typed records and the server's raw API
 * rows both satisfy them.
 *
 * @module platform-shared/findingQueue
 */
import { isGapSuggestionKind } from "./findingPlanning.ts";
import { isOpenSuggestionStatus } from "./reviewCounts.ts";

export type FindingQueueSeverity = "critical" | "high" | "medium" | "low";
export type FindingQueueStatus = "open" | "resolved" | "dismissed";
export type FindingQueueBasis = "regulation" | "control" | "missing";

/** Where a missing document's gap is filed: it has no document yet. */
export const NOT_IN_THE_LIBRARY = "Not in the library";

export const toFindingSeverity = (value: string | null | undefined): FindingQueueSeverity | null =>
  value === "critical" || value === "high" || value === "medium" || value === "low" ? value : null;

/** A regulatory finding's queue status: resolved, closed → dismissed, else open. */
export const findingQueueStatus = (status: string): FindingQueueStatus =>
  status === "resolved" ? "resolved" : status === "closed" ? "dismissed" : "open";

/** A gap suggestion's queue status: open or accepted → open, applied → resolved. */
export const suggestionQueueStatus = (status: string): FindingQueueStatus =>
  isOpenSuggestionStatus(status) ? "open" : status === "applied" ? "resolved" : "dismissed";

/** What a suggestion is a gap of, or null when it is a record update, not a finding. */
export const suggestionBasis = (kind: string): Exclude<FindingQueueBasis, "regulation"> | null =>
  !isGapSuggestionKind(kind) ? null : kind === "missing_document" ? "missing" : "control";

/** A library document's name as the library shows it: its title, else its file name. */
export const libraryDocumentName = (policy: {
  readonly title?: string | null;
  readonly filename: string;
}): string =>
  policy.title?.trim() ? policy.title.trim() : policy.filename.replace(/\.pdf$/i, "").replace(/_+/g, " ");

/** The document a suggestion's gap is filed under. */
export const suggestionDocumentName = (
  suggestion: { readonly policyId: string | null; readonly policyName?: string | null },
  documentNames: ReadonlyMap<string, string>,
): string =>
  suggestion.policyId
    ? (documentNames.get(suggestion.policyId) ?? suggestion.policyName ?? "Document")
    : NOT_IN_THE_LIBRARY;

const proposedText = (proposedChange: Readonly<Record<string, unknown>>, key: string): string | null => {
  const value = proposedChange[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
};

/** The control code a control-gap suggestion names, when it names one. */
export const suggestionControlCode = (proposedChange: Readonly<Record<string, unknown>>): string | null =>
  proposedText(proposedChange, "controlCode");

/** A gap suggestion's required change: its amendment, prefixed by the section it lands in. */
export const suggestionRequiredChanges = (
  proposedChange: Readonly<Record<string, unknown>>,
): ReadonlyArray<string> => {
  const section = proposedText(proposedChange, "section");
  const amendment = proposedText(proposedChange, "amendment");
  return amendment ? [section ? `${section}: ${amendment}` : amendment] : [];
};
